import { all, put, call, getContext, takeLatest } from 'redux-saga/effects';
import { push } from 'connected-react-router';

import { Logger } from 'utils/Logger';
import { Analytics } from 'utils/Analytics';
import { FundingMatchStatus } from 'enums/funding-match-status';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';

import { declineFundingMatch, acceptLead, rejectLead } from './types';
import {
  requestDeclineFundingMatchSuccess,
  requestDeclineFundingMatchFailure,
  closeDeclineFundingMatchModal,
} from './reducer';

import { requestFundingSearchActivities } from '../activities/reducer';
import * as fundingSearchProfileActions from '../fundingSearchProfile/reducer';
import { requestFundingSearch, requestFundingSearchContactDetails } from '../fundingSearch/reducer';

import updateMatchStatusModalSaga from './updateMatchStatusModal/sagas';

const logger = new Logger('Saga:FundingMatch');

const getFundingRouteConfig = ({ fundingMatchId, fundingSearchId }) => ({
  params: fundingMatchId ? { matchId: fundingMatchId } : { fundingSearchId },
  path: fundingMatchId ? 'api.platform.funding_matches' : 'api.funding_search_update_funding_match',
});

export function* requestDeclineFundingMatchSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');

  try {
    const response = yield call(
      authApiService.patch,
      routerService.generate('api.activities.funding_match_decline', {
        activityId: action.activityId,
      }),
      { reason: action.declineReason },
    );

    action.promise && action.promise.resolve(response.data);
    yield put(requestDeclineFundingMatchSuccess(action.activityId, response.data));
    yield put(closeDeclineFundingMatchModal());
    yield put(requestFundingSearchActivities(action.fundingSearchId));
    yield put(requestFundingSearch(action.fundingSearchId));
  } catch (error) {
    logger.error('Failed to request cancel funding match with the following error', error);
    action.reject && action.promise.reject(error);
    yield put(requestDeclineFundingMatchFailure(error));
  }
}

export function* requestAcceptFundingMatchSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  const { params, path } = getFundingRouteConfig(action);

  try {
    yield call(
      action.fundingMatchId ? authApiService.patch : authApiService.put,
      routerService.generate(path, params),
      { status: FundingMatchStatus.inReview },
    );

    Analytics.track(
      AnalyticsEventCategory.fundingSearchProfile,
      AnalyticsEventAction.profileLeadAccepted,
      { extraParams: params },
    );

    yield put(requestFundingSearch(action.fundingSearchId));
    yield put(fundingSearchProfileActions.requestFundingSearchProfile(action.fundingSearchId));
    yield put(requestFundingSearchContactDetails(action.fundingSearchId));
  } catch (error) {
    logger.error("Failed to update status of funding search's funding match", error);
  }
}

export function* requestRejectFundingMatchSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  const { params, path } = getFundingRouteConfig(action);

  try {
    yield call(
      action.fundingMatchId ? authApiService.patch : authApiService.put,
      routerService.generate(path, params),
      {
        status: FundingMatchStatus.passed,
        passReason: action.reason,
        passComment: action.comment,
      },
    );

    Analytics.track(
      AnalyticsEventCategory.fundingSearchProfile,
      AnalyticsEventAction.profileLeadPassed,
      { extraParams: params },
    );

    yield put(push('/funding'));
  } catch (error) {
    logger.error("Failed to update status of funding search's funding match", error);
  }
}

export default function* fundingSearchSaga() {
  return yield all([
    takeLatest(declineFundingMatch.request, requestDeclineFundingMatchSaga),
    takeLatest(acceptLead.request, requestAcceptFundingMatchSaga),
    takeLatest(rejectLead.request, requestRejectFundingMatchSaga),
    updateMatchStatusModalSaga(),
  ]);
}
